import { ChangeDetectorRef, Component, Input, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import * as _ from 'lodash';
import { UsersStore } from '@frontend-site/shared/core';
import { PFieldAbstractComponent } from '../p-field-abstract';
import { MatMenuTrigger } from '@angular/material/menu';
import { TranslateService } from '@ngx-translate/core';
import { KeyboardService } from '../../keyboard';
import { ListItem } from '../p-field-single-select';
import { resolveUnconnectedUserDisplay, textOnlyDisplay, UnconnectedUsers, UserDisplay } from '../unconnected-users/unconnected-user-display';

@Component({
    selector: 'p-field-user',
    styleUrls: ['./p-field-user.component.scss', '../p-field-single-select/p-field-single-select.component.scss'],
    template: ` <p-field
        [style]="dynamicStyle"
        [isMandatory]="isMandatory"
        [label]="label"
        [labelTooltip]="labelTooltip"
        [isReadOnly]="isReadOnly"
        [shouldTransformLabelTextCase]="shouldTransformLabelTextCase"
        *ngIf="{ disconnectedUsers: disconnectedUsers$ | async, inactiveUsers: inactiveUsers$ | async } as unconnectedUsers"
    >
        <div slot="readMode">
            <mat-form-field class="p-input-text" appearance="fill" [class.p-input-text-readonly]="isReadOnly">
                <p-icon
                    class="p-input-link-prefix"
                    matPrefix
                    *ngIf="getUserIcon(value, unconnectedUsers) as icon"
                    [name]="icon"
                    size="sm"
                    [matTooltip]="getUserTooltip(value, unconnectedUsers)"
                ></p-icon>
                <input
                    [attr.automation-id]="'p-field-user_' + label"
                    matInput
                    readonly
                    type="text"
                    class="p-input-text-input"
                    [placeholder]="placeholder | sentenceCase"
                    [value]="getUserValue(value, unconnectedUsers)"
                    [disabled]="isDisabled"
                    [matTooltip]="getUserValue(value, unconnectedUsers)"
                    [matTooltipDisabled]="isDisabled"
                />
            </mat-form-field>
        </div>
        <div slot="editMode">
            <mat-form-field class="p-input-text" appearance="fill" [mat-menu-trigger-for]="optionsMenu">
                <p-icon
                    class="p-input-link-prefix"
                    matPrefix
                    *ngIf="getUserIcon(value, unconnectedUsers) as icon"
                    [name]="icon"
                    size="sm"
                    [matTooltip]="getUserTooltip(value, unconnectedUsers)"
                ></p-icon>
                <input
                    #inputField
                    [attr.automation-id]="'p-field-user_' + label"
                    matInput
                    readonly
                    type="text"
                    class="p-input-text-input"
                    [placeholder]="placeholder | sentenceCase"
                    [value]="getUserValue(value, unconnectedUsers)"
                    [matTooltip]="getUserValue(value, unconnectedUsers)"
                    [matTooltipDisabled]="value?.id === 0"
                    (keydown)="onKeydownEvent($event)"
                />
                <p-icon class="p-field-input-icon" iconName="chevron-down" size="sm"></p-icon>
                <mat-menu
                    #optionsMenu="matMenu"
                    (click)="$event.stopPropagation()"
                    [class]="menuCustomClasses + ' no-padding embedded p-menu'"
                >
                    <ng-template matMenuContent>
                        <div fxLayout="column" (click)="$event.stopImmediatePropagation()">
                            <p-picker-single-select
                                [style]="menuDynamicStyle"
                                [emptyOption]="emptyOption"
                                [autoFocus]="true"
                                [list]="list"
                                [idField]="idField"
                                [nameField]="nameField"
                                [value]="getUserId(value)"
                                (valueChange)="changeValue($event)"
                                [canSearch]="canSearch"
                                [noData]="noData"
                                placeholder="search.."
                                customClasses="no-padding"
                            >
                            </p-picker-single-select>
                        </div>
                    </ng-template>
                </mat-menu>
            </mat-form-field>
        </div>
    </p-field>`,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: PFieldUserComponent
        }
    ],
    exportAs: 'pFieldUserSingleSelect'
})
export class PFieldUserComponent extends PFieldAbstractComponent<UserKeyValue> {
    @Input() label?: string;
    @Input() formControl: any;
    @Input() isMandatory = false;
    @Input() override isReadOnly = false;
    @Input() canSearch = false;
    @Input() list: ListItem[] = [];
    @Input() customClasses?: string;
    @Input() menuCustomClasses?: string;
    @Input() noData = this.translate.instant('UserManagement.UserAvatar.noData');
    @Input() isInEditMode = true;
    @Input() placeholder?: string;
    @Input() idField = 'id';
    @Input() nameField = 'name';
    @Input() autoFocus = false;
    @Input() emptyOption: any = { id: 0, name: this.translate.instant('UserManagement.UserAvatar.emptyOption') };
    @Input() autoOpen = false;
    @Input() public dynamicStyle: { [klass: string]: any } | null | undefined = undefined;
    @Input() public menuDynamicStyle: { [klass: string]: any } | null | undefined = undefined;

    @ViewChild(MatMenuTrigger) matSelect: MatMenuTrigger = {} as MatMenuTrigger;

    constructor(
        public usersStore: UsersStore,
        cd: ChangeDetectorRef,
        private translate: TranslateService,
        private keyboardService: KeyboardService
    ) {
        super(cd);
    }

    disconnectedUsers$ = this.usersStore.disconnectedUsers$;
    inactiveUsers$ = this.usersStore.inactiveUsers$;

    getUserId(value: any) {
        return value?.id ?? value;
    }

    getUserFromUsersList(value: any) {
        return _.find(this.list as any, { [this.idField]: value })?.[this.nameField];
    }

    isEmpty(userValue: any): Boolean {
        try {
            return userValue ? (typeof userValue === 'object' ? !userValue[this.idField] : false) : true;
        } catch (e) {
            return false;
        }
    }

    getUserValue(value: any, unconnectedUsers: UnconnectedUsers): string {
        return this.resolveDisplay(value, unconnectedUsers).text;
    }

    getUserIcon(value: any, unconnectedUsers: UnconnectedUsers): string | null {
        return this.resolveDisplay(value, unconnectedUsers).icon;
    }

    getUserTooltip(value: any, unconnectedUsers: UnconnectedUsers): string {
        return this.resolveDisplay(value, unconnectedUsers).tooltip;
    }

    /**
     * Pure derivation of what the field shows for `value` (text + prefix icon + tooltip).
     * It must stay free of side effects: it is evaluated from template bindings, and the icon's `*ngIf` and
     * `[name]` both read it, so binding order can never hand a stale/null icon name to `p-icon` (IN-01004).
     */
    private resolveDisplay(value: any, unconnectedUsers: UnconnectedUsers): UserDisplay {
        const userId = this.getUserId(value);

        if (this.isReadOnly && this.isEmpty(userId)) {
            return textOnlyDisplay('');
        }
        if (!userId || this.emptyOption?.[this.idField] === userId) {
            return textOnlyDisplay(this.emptyOption?.[this.nameField]);
        }
        const listedName = this.getUserFromUsersList(userId);
        if (listedName != null) {
            return textOnlyDisplay(listedName);
        }
        return resolveUnconnectedUserDisplay(userId, unconnectedUsers, this.translate) ?? textOnlyDisplay('PANAYA');
    }

    changeValue(userId: number) {
        this.markAsTouched();

        if (!this.isReadOnly) {
            this.value = { id: userId, name: this.getUserFromUsersList(userId) };
            this.matSelect?.closeMenu();
            this._onChange(this.value);
        }
    }

    // tslint:disable-next-line:use-lifecycle-interface
    ngAfterViewInit(): void {
        if (this.autoOpen) {
            this.matSelect?.openMenu();
        }
    }

    onKeydownEvent(e: KeyboardEvent) {
        const code = e.keyCode;

        if (this.keyboardService.isDown(code) || this.keyboardService.isEnter(code)) {
            this.matSelect?.openMenu();
        }
    }
}

interface UserKeyValue {
    id: number;
    name: string;
}
